import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fivemLogAPI } from '../services/api';
import { FiClock, FiUsers, FiTrendingUp, FiAward, FiCalendar } from 'react-icons/fi';
import '../styles/ModShiftSummary.css';

const fmtHours = (min) => {
    if (!min) return '0h';
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    if (h === 0) return `${m}p`;
    return m === 0 ? `${h}h` : `${h}h ${m}p`;
};

const dayShortLabel = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const dows = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
    return `${dows[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
};

const ModShiftSummary = () => {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(7);
    const [loading, setLoading] = useState(true);

    const fetch = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fivemLogAPI.getModShiftSummary({ days });
            setData(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { fetch(); }, [fetch]);
    useEffect(() => {
        const id = setInterval(fetch, 30_000);  // refresh 30s
        return () => clearInterval(id);
    }, [fetch]);

    if (loading && !data) return <div className="mss-loading">Đang tải thống kê...</div>;
    if (!data) return null;

    const maxHourMin = Math.max(...data.byHour, 1);
    const maxDayMin  = Math.max(...data.byDay.map(d => d.minutes), 1);

    return (
        <div className="mss-wrap">
            {/* Header với chọn period */}
            <div className="mss-header">
                <h2><FiCalendar /> Thống Kê Lịch Trực Mod</h2>
                <div className="mss-period-tabs">
                    {[7, 14, 30].map(d => (
                        <button key={d}
                            className={`mss-period-btn ${days === d ? 'active' : ''}`}
                            onClick={() => setDays(d)}>
                            {d} ngày
                        </button>
                    ))}
                </div>
            </div>

            {/* Summary cards */}
            <div className="mss-cards">
                <div className="mss-card mss-card-primary">
                    <div className="mss-card-icon"><FiClock /></div>
                    <div>
                        <div className="mss-card-label">Tổng giờ trực ({days}d)</div>
                        <div className="mss-card-val">{fmtHours(data.totalMinutes)}</div>
                        <div className="mss-card-sub">{data.shiftCount} ca · {data.activeCount} đang trực</div>
                    </div>
                </div>

                <div className="mss-card">
                    <div className="mss-card-icon"><FiTrendingUp /></div>
                    <div>
                        <div className="mss-card-label">Khung giờ trực nhiều nhất</div>
                        <div className="mss-card-val">{data.peakHourLabel}</div>
                        <div className="mss-card-sub">{fmtHours(data.byHour[data.peakHour])} tổng</div>
                    </div>
                </div>

                <div className="mss-card">
                    <div className="mss-card-icon"><FiCalendar /></div>
                    <div>
                        <div className="mss-card-label">Thứ trực nhiều nhất</div>
                        <div className="mss-card-val">{data.peakDay}</div>
                        <div className="mss-card-sub">{fmtHours(data.byDayOfWeek[data.dayLabels.indexOf(data.peakDay)])} trong tuần</div>
                    </div>
                </div>

                <div className="mss-card">
                    <div className="mss-card-icon"><FiUsers /></div>
                    <div>
                        <div className="mss-card-label">Số mod đã trực</div>
                        <div className="mss-card-val">{data.byAdmin.length}</div>
                        <div className="mss-card-sub">{data.activeCount > 0 && `${data.activeCount} đang online`}</div>
                    </div>
                </div>
            </div>

            {/* Day breakdown bar chart */}
            <div className="mss-section">
                <h3>📅 Theo từng ngày</h3>
                <div className="mss-day-chart">
                    {data.byDay.map(d => (
                        <div key={d.date} className="mss-day-col" title={`${d.date}: ${fmtHours(d.minutes)}`}>
                            <div className="mss-day-bar-wrap">
                                <div className="mss-day-bar" style={{ height: `${(d.minutes / maxDayMin) * 100}%` }} />
                            </div>
                            <div className="mss-day-val">{fmtHours(d.minutes)}</div>
                            <div className="mss-day-label">{dayShortLabel(d.date)}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Hour heatmap */}
            <div className="mss-section">
                <h3>🕐 Khung giờ trực (0h → 23h) <small style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.72rem' }}>· giờ đã qua hôm nay tô đen, giờ hiện tại có viền vàng</small></h3>
                <div className="mss-hour-chart">
                    {data.byHour.map((min, h) => {
                        const intensity = min / maxHourMin;
                        const currentHour = new Date().getHours();
                        const isPast    = h < currentHour;
                        const isCurrent = h === currentHour;
                        const isFuture  = h > currentHour;

                        let background;
                        let cellClass = 'mss-hour-cell';
                        if (isPast) {
                            background = 'rgba(0,0,0,0.55)';   // đen — giờ đã qua hôm nay
                            cellClass += ' is-past';
                        } else {
                            background = intensity > 0
                                ? `rgba(16,185,129,${0.15 + intensity * 0.6})`
                                : 'var(--bg-tertiary)';
                        }
                        if (isCurrent) cellClass += ' is-current';
                        if (h === data.peakHour) cellClass += ' peak';

                        return (
                            <div key={h}
                                className={cellClass}
                                style={{ background }}
                                title={`${String(h).padStart(2,'0')}:00 - ${fmtHours(min)}${isPast ? ' (đã qua)' : isCurrent ? ' (đang trong giờ này)' : ''}`}>
                                <div className="mss-hour-time">{String(h).padStart(2,'0')}h</div>
                                <div className="mss-hour-val">{fmtHours(min)}</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Top admins */}
            {data.byAdmin.length > 0 && (
                <div className="mss-section">
                    <h3><FiAward /> Top mod trực nhiều nhất</h3>
                    <table className="mss-admin-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Tên</th>
                                <th>Số ca</th>
                                <th>Tổng giờ</th>
                                <th>TB / ca</th>
                                <th>Trạng thái</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.byAdmin.slice(0, 15).map((a, i) => (
                                <tr key={a.name}>
                                    <td className="mss-rank">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}</td>
                                    <td><span className="mss-admin-name">{a.name}</span></td>
                                    <td>{a.shiftCount}</td>
                                    <td><strong>{fmtHours(a.totalMinutes)}</strong></td>
                                    <td>{fmtHours(Math.round(a.totalMinutes / a.shiftCount))}</td>
                                    <td>
                                        {a.isActive
                                            ? <span className="mss-active">🟢 đang trực</span>
                                            : <span className="mss-offline">⚫ offline</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ModShiftSummary;
